import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMemo, useEffect } from "react";
import Image from "src/components/image";
import {
  Avatar,
  Box,
  MenuItem,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import Stack from "@mui/system/Stack";

import FormProvider, {
  RHFAutocomplete,
  RHFSelect,
} from "src/components/hook-form";
import { useGetUserProviderProfiles } from "src/api/provider";
import { useAuthContext } from "src/auth/hooks";
import { IProfileSelectorProvider } from "src/types/sportprovider";
import { t } from "i18next";
import { usePathname, useRouter } from "src/routes/hooks";
import { paths } from "src/routes/paths";

type Props = {
  currentProvider: number;
  currentProviderType: string;
  providers: IProfileSelectorProvider[];
  updateProvider(id: number, type: string): void;
};

export default function ProfileSelector({
  currentProvider,
  currentProviderType,
  providers,
  updateProvider,
}: Props) {
  const router = useRouter();
  const pathname = usePathname();

  const ProfileSchema = Yup.object({
    profile: Yup.number().required("Id is required"),
    type: Yup.string().required("Type is required"),
  });

  // stable first option (guards against providers changing)
  // const first = useMemo(() => providers?.[0] ?? null, [providers]);
  const first = useMemo(() => {
    if (!providers || providers.length === 0) return null;

    // if the first one is a dummy (id === 0) and we have more, use the second
    if (providers[0].id === 0 && providers.length > 1) {
      return providers[1];
    }

    return providers[0];
  }, [providers]);

  const methods = useForm({
    resolver: yupResolver(ProfileSchema),
    defaultValues: {
      profile: currentProvider ?? first?.id ?? undefined,
      type: currentProviderType ?? first?.type ?? "",
    },
    mode: "onChange",
  });

  const { setValue, watch, getValues } = methods;

  const selectedId = watch("profile") as number | undefined;
  const selectedOption =
    providers.find((p) => p.id === Number(selectedId)) ?? null;

  // Bootstrap defaults if nothing selected but we have at least one provider
  useEffect(() => {
    if (!selectedId && first) {
      setValue("profile", first.id, {
        shouldDirty: false,
        shouldValidate: true,
      });
      setValue("type", first.type, {
        shouldDirty: false,
        shouldValidate: true,
      });
      updateProvider(first.id, first.type);
      localStorage.setItem("currentProvider", String(first.id));
      localStorage.setItem("currentProviderType", first.type);
      router.push(paths.dashboard.root);
    }
  }, [selectedId, first, setValue, updateProvider, router]);

  // If the currently selected id disappears from the list, fall back to first
  useEffect(() => {
    if (selectedId && !selectedOption && first) {
      setValue("profile", first.id, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue("type", first.type, { shouldDirty: true, shouldValidate: true });
      updateProvider(first.id, first.type);
      localStorage.setItem("currentProvider", String(first.id));
      localStorage.setItem("currentProviderType", first.type);
      router.push(paths.dashboard.root);
    }
  }, [selectedId, selectedOption, first, setValue, updateProvider, router]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const id = Number(e.target.value); // Select gives a string
    const option = providers.find((p) => p.id === id);
    if (!option) return;

    const prevId = Number(methods.getValues("profile"));
    const prevType = methods.getValues("type");

    setValue("profile", option.id, { shouldDirty: true, shouldValidate: true });
    setValue("type", option.type, { shouldDirty: true, shouldValidate: true });

    updateProvider(option.id, option.type);
    localStorage.setItem("currentProvider", String(option.id));
    localStorage.setItem("currentProviderType", option.type);

    if (option.type !== prevType) {
      router.push(paths.dashboard.root);
    } else if (
      option.id !== prevId &&
      pathname.startsWith(`${paths.dashboard.organiser.tournament.root}/`)
    ) {
      router.push(paths.dashboard.organiser.tournaments);
    }
  };

  return (
    <FormProvider methods={methods}>
      <Stack direction="row">
        <RHFSelect
          name="profile"
          label={t("profile")}
          onChange={handleChange}
          SelectProps={{
            MenuProps: { keepMounted: true, disableScrollLock: true },
          }}
          sx={{
            mt: 1,
            maxWidth: { xs: 200, sm: 300 },
            minWidth: { xs: 200, sm: 300 },
          }}
        >
          {providers.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ width: 1 }}
              >
                <Avatar
                  src={option.profileImage}
                  alt={option.displayName}
                  sx={{ width: 30, height: 30 }}
                />
                <Typography variant="body2" noWrap>
                  {option.displayName}
                </Typography>
                <span style={{ marginLeft: "auto" }}>
                  <img
                    alt="type"
                    src={
                      option.type === "facility"
                        ? "/assets/images/court.png"
                        : option.type === "coach"
                        ? "/assets/images/whistle.png"
                        : "/assets/images/trophy.png"
                    }
                    width={24}
                    height={24}
                    draggable={false}
                    style={{ pointerEvents: "none" }}
                  />
                </span>
              </Stack>
            </MenuItem>
          ))}
        </RHFSelect>
      </Stack>
    </FormProvider>
  );
}

{
  /* <RHFAutocomplete<IProfileSelectorProvider, false, false, false>
          name="profile"
          label={t("providers")}
          options={providers}
          // show the object, but store its id in the form
          value={selectedOption}
          onChange={handleChange}
          getOptionLabel={(option) => option?.displayName ?? ""}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          ListboxProps={{
            onMouseDown: (e) => e.preventDefault(), // <- keeps focus so tap selects
          }}
          // disablePortal // helps on some mobile setups
          sx={{
            maxWidth: { xs: 200, sm: 300 },
            minWidth: { xs: 200, sm: 300 },
            "& .MuiAutocomplete-clearIndicator": { display: "none" },
          }}
          renderOption={(props, option) => (
            <li
              {...props}
              key={option.id}
              onMouseDown={(e) => e.preventDefault()}
            >
              <Avatar
                src={option.profileImage}
                alt={option.displayName}
                sx={{ width: 30, height: 30, mr: 1 }}
              />
              {option.displayName}
              
              <Image
                alt="type"
                src={
                  option.type === "facility"
                    ? "/assets/images/court.png"
                    : option.type === "coach"
                    ? "/assets/images/whistle.png"
                    : "/assets/images/trophy.png"
                }
                ratio="1/1"
                sx={{ width: 30, ml: "auto" }}
              />
            </li>
          )}
        /> */
}
